import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useActiviteitenSearch'
import type { ActiviteitenSearchFilters as Filters } from '../types/ActiviteitenSearch'

const kpiTooltips = {
  red: 'Laat alleen de verlopen activiteiten zien',
  amber: 'Laat bijna verlopen activiteiten zien',
  green: 'Laat alle activiteiten zien die niet (bijna) verlopen zijn',
  grey: 'Laat alle activiteiten zien',
}

interface Props {
  onChange: (filters: Filters) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    actiehouders: GroupedFilterOption[]
    gebieden: Gebied[]
    activiteittypes: FilterOption[]
    labels: FilterOption[]
  }
  showToegewezenAan: boolean
}

export const ActiviteitenFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  filterOptions: {
    actiehouders,
    gebieden,
    activiteittypes,
    labels: valueListLabels,
  },
  showToegewezenAan,
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    gebied,
    type,
    assignedTo,
    fromDate,
    toDate,
    fromChangedDate,
    toChangedDate,
    fromGeplandeUitvoeringsdatum,
    toGeplandeUitvoeringsdatum,
    labels,
    myList,
    kpi,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          multiple
          options={activiteittypes}
          label="Type activiteit"
          filterKey="type"
          value={type}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {showToegewezenAan && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={actiehouders}
            label="Toegewezen aan"
            filterKey="assignedTo"
            value={assignedTo}
            onFilterChange={handleFilterChange}
            groupByProp="group"
          />
        </FilterGridItem>
      )}

      {valueListLabels.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={valueListLabels}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={fromGeplandeUitvoeringsdatum}
          toDate={toGeplandeUitvoeringsdatum}
          onFilterChange={handleFilterChange}
          label="Project gepland"
          prop="GeplandeUitvoeringsdatum"
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Opleverdatum"
          fromDate={fromDate}
          toDate={toDate}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Wijzigingsdatum"
          fromDate={fromChangedDate}
          toDate={toChangedDate}
          prop="ChangedDate"
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi
          onFilterChange={handleFilterChange}
          kpi={kpi}
          tooltips={kpiTooltips}
        />
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Zoeken"
          onClick={handleZoeken}
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
